"use client"

import * as React from "react"
import { VaultIcon, DealPipelineIcon } from "@/components/sidebar/icons"
import { useState, useEffect, useCallback, useMemo } from "react"
import { Briefcase, FileSignature, FileText, PackageCheck, PlayCircle, Users } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { NavMain } from "@/components/sidebar/nav-main"
import { NavProjects } from "@/components/sidebar/nav-projects"
import { NavQuickAccess } from "@/components/sidebar/nav-quick-access"
import { NavContracts } from "@/components/sidebar/nav-contracts"
import { NavUser } from "@/components/sidebar/nav-user"
import { NavPortfolio } from "@/components/sidebar/nav-portfolio"
import { TeamSwitcher } from "@/components/sidebar/team-switcher"
import { SearchCommand, useSearchCommand } from "@/components/sidebar/search-command"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar"
import { useProfile } from "src/hooks/profile/useProfile"
import { useProfilePicture } from "src/hooks/profile/useProfilePicture"
import { useDemoOrg } from "src/contexts/DemoOrgContext"
import DEFAULT_UNKNOWN_PROFILE_PICTURE from "src/assets/img/DefaultUnknownProfilePicture.png"
import { UserProfile } from "src/types/common.types"
import { persistStore } from "src/persist-store"
import { PATH_ROOT } from "src/routes/paths"
import { FeatureFlags } from "@/hooks/common/useFeatureFlag"
import { useSelectedOrgId } from "@/hooks/common/useSelectedOrgId"
import {
  QuickAccessItem,
  QuickAccessPrefsV1,
  applyQuickAccessPrefs,
  getQuickAccessStorageKey,
  toQuickAccessPrefs,
} from "@/components/sidebar/quick-access-prefs"
import { PERSONAL_ORG_ID, QUICK_ACCESS_DEALS } from "@/mockData"

const data = {
  teams: [
    {
      name: "November",
      logo: FileText,
      plan: "Enterprise",
    },
  ],
  projects: [],
}

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  isDemo?: boolean
}

export function AppSidebar({ isDemo = false, ...props }: AppSidebarProps) {
  const { profile } = useProfile()
  const { profilePictureURL } = useProfilePicture()
  const demoContext = useDemoOrg()
  const selectedOrgId = useSelectedOrgId()
  const navigate = useNavigate()
  const { open: searchOpen, setOpen: setSearchOpen } = useSearchCommand()

  const activeQuickAccessOrgId = isDemo ? selectedOrgId || PERSONAL_ORG_ID : PERSONAL_ORG_ID
  const initialQuickAccessItems = useMemo<QuickAccessItem[]>(
    () =>
      (QUICK_ACCESS_DEALS[activeQuickAccessOrgId] ?? QUICK_ACCESS_DEALS[PERSONAL_ORG_ID]).map(
        (item) => ({
          id: item.dealId,
          name: item.name,
          url: `${PATH_ROOT.demo.deal}/${item.dealId}`,
          isPinned: item.isPinned,
        }),
      ),
    [activeQuickAccessOrgId],
  )

  const [fallbackProfile, setFallbackProfile] = useState<UserProfile | null>(null)
  const [fallbackImageUrl, setFallbackImageUrl] = useState(DEFAULT_UNKNOWN_PROFILE_PICTURE)
  const [quickAccessItems, setQuickAccessItems] = useState<QuickAccessItem[]>(
    initialQuickAccessItems,
  )
  const [hydratedQuickAccessStorageKey, setHydratedQuickAccessStorageKey] =
    useState<string | null>(null)

  const demoProfile = useMemo(
    () => (isDemo && demoContext ? demoContext.getCurrentProfile() : null),
    [isDemo, demoContext],
  )
  const resolvedUserProfile: UserProfile | { displayName?: string; email?: string; profileImage?: string } | null =
    demoProfile || profile || fallbackProfile
  const resolvedUserEmail = resolvedUserProfile?.email || ""
  const quickAccessStorageKey = useMemo(
    () =>
      getQuickAccessStorageKey(
        isDemo ? `${resolvedUserEmail}:${activeQuickAccessOrgId}` : resolvedUserEmail,
      ),
    [activeQuickAccessOrgId, isDemo, resolvedUserEmail],
  )
  const resolvedProfileImageUrl = demoProfile
    ? demoProfile.profileImage || "/clipse.webp"
    : profilePictureURL || fallbackImageUrl

  useEffect(() => {
    const loadFallbackData = async () => {
      try {
        if (!profile) {
          const profileData = await persistStore.getSession<UserProfile>('userProfile')
          if (profileData) {
            setFallbackProfile(profileData)
          }
        }

        if (!profilePictureURL) {
          const profilePicture = await persistStore.getSession<string>('profilePictureURL')
          if (profilePicture) {
            setFallbackImageUrl(profilePicture)
          }
        }
      } catch (error) {
        console.error('Error loading profile data:', error)
      }
    }

    loadFallbackData()
  }, [profile, profilePictureURL])

  useEffect(() => {
    let isMounted = true
    setHydratedQuickAccessStorageKey(null)

    const loadQuickAccessPrefs = async () => {
      try {
        const prefs =
          await persistStore.getLocal<QuickAccessPrefsV1>(quickAccessStorageKey)

        if (!isMounted) {
          return
        }

        setQuickAccessItems(applyQuickAccessPrefs(initialQuickAccessItems, prefs))
      } catch (error) {
        console.error("Error loading quick access preferences:", error)
        if (isMounted) {
          setQuickAccessItems(initialQuickAccessItems)
        }
      } finally {
        if (isMounted) {
          setHydratedQuickAccessStorageKey(quickAccessStorageKey)
        }
      }
    }

    loadQuickAccessPrefs()

    return () => {
      isMounted = false
    }
  }, [initialQuickAccessItems, quickAccessStorageKey])

  useEffect(() => {
    if (hydratedQuickAccessStorageKey !== quickAccessStorageKey) {
      return
    }

    persistStore
      .putLocal(quickAccessStorageKey, toQuickAccessPrefs(quickAccessItems))
      .catch((error) => {
        console.error("Error saving quick access preferences:", error)
      })
  }, [
    hydratedQuickAccessStorageKey,
    quickAccessItems,
    quickAccessStorageKey,
  ])

  const handleQuickAccessReorder = useCallback((items: QuickAccessItem[]) => {
    setQuickAccessItems(items)
  }, [])

  const handleQuickAccessTogglePin = useCallback((itemId: string) => {
    setQuickAccessItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, isPinned: !item.isPinned } : item
      )
    )
  }, [])

  // Ensure displayName is always populated, using email as fallback
  let displayName = ''
  if (resolvedUserProfile?.displayName) {
    displayName = resolvedUserProfile.displayName
  } else if (resolvedUserProfile?.email) {
    // Extract username from email
    displayName = resolvedUserProfile.email.split('@')[0]
  }

  const userEmail = resolvedUserEmail
  const imgSrc = resolvedProfileImageUrl || DEFAULT_UNKNOWN_PROFILE_PICTURE

  const userData = {
    name: displayName,
    email: userEmail,
    profileImageUrl: imgSrc,
  }

  const navMainItems = useMemo(
    () => [
      {
        label: "Deal Pipeline",
        icon: DealPipelineIcon,
        url: isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline,
        flag: isDemo ? FeatureFlags.DEMO_DEAL_PIPELINE : FeatureFlags.PROD_DEAL_PIPELINE,
      },
      {
        label: "Vault",
        icon: VaultIcon,
        url: isDemo ? PATH_ROOT.demo.vault : PATH_ROOT.main.vault,
        flag: isDemo ? FeatureFlags.DEMO_VAULT : FeatureFlags.PROD_VAULT,
      },
      {
        label: "Contract IQ",
        icon: FileSignature,
        url: isDemo ? PATH_ROOT.demo.contractIq : PATH_ROOT.main.contractIq,
        flag: isDemo ? FeatureFlags.DEMO_CONTRACT_IQ : FeatureFlags.PROD_CONTRACT_IQ,
      },
      {
        label: "People",
        icon: Users,
        url: isDemo ? PATH_ROOT.demo.people : PATH_ROOT.main.people,
        flag: isDemo ? FeatureFlags.DEMO_PEOPLE : FeatureFlags.PROD_PEOPLE,
      },
      {
        label: "Delivery Manager",
        icon: PackageCheck,
        url: isDemo ? PATH_ROOT.demo.deliveryManager : PATH_ROOT.main.deliveryManager,
        flag: isDemo ? FeatureFlags.DEMO_DELIVERY_MANAGER : FeatureFlags.PROD_DELIVERY_MANAGER,
      },
      {
        label: isDemo ? "Roster" : "Portfolio",
        icon: Briefcase,
        url: isDemo ? PATH_ROOT.demo.roster : PATH_ROOT.main.portfolio,
        flag: isDemo ? FeatureFlags.DEMO_PORTFOLIO : FeatureFlags.PROD_PORTFOLIO,
      },
    ],
    [isDemo],
  )

  return (
    <>
    <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>

        <NavMain items={navMainItems} onSearchClick={() => setSearchOpen(true)} isDemo={isDemo} />
        {isDemo && (
          <NavQuickAccess
            items={quickAccessItems}
            onReorder={handleQuickAccessReorder}
            onTogglePin={handleQuickAccessTogglePin}
          />
        )}
        {/* <NavContracts /> */}
        <NavProjects projects={data.projects} />
      </SidebarContent>
      <SidebarGroup>
          <SidebarMenu>
            
            {isDemo && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate(PATH_ROOT.demo.interactive)}
                  tooltip="Interactive Demo"
                  className="bg-neutral-800 text-white dark:bg-white dark:text-neutral-800"
                >
                  <PlayCircle className="size-4" />
                  <span>Interactive Demo</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            
          </SidebarMenu>
        </SidebarGroup>
      <SidebarFooter>
        {isDemo && <NavPortfolio />}
        <NavUser user={userData} isDemo={isDemo} />
      </SidebarFooter>
      {/* <SidebarRail /> */}
    </Sidebar>
    </>
  )
}
